import { ApiError } from "@/clients/core/ApiError";
import { SubscriptionStatus } from "@/clients/models/SubscriptionStatus";
import type { SubscriptionStatusResponse } from "@/clients/models/SubscriptionStatusResponse";
import { captureException, captureMessage } from "@sentry/react";

/**
 * Logical contradictions in a subscription-status payload that can drive the
 * paywall to the wrong decision. These are backend data-quality signals — the
 * client cannot fix them, but it should surface them so they get noticed.
 */
export type SubscriptionInconsistency =
  | "active_and_expired"
  | "entitled_flag_terminal_status"
  | "trial_not_active"
  | "inactive_flag_active_status";

// Statuses that mean "not entitled". `is_active === true` alongside any of these
// is a contradiction, and because `is_active` short-circuits the paywall it
// produces a free rider.
const TERMINAL_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.NONE,
  SubscriptionStatus.CANCELLED,
  SubscriptionStatus.EXPIRED,
  SubscriptionStatus.BILLING_ISSUE,
]);

export const detectSubscriptionInconsistencies = (
  status: SubscriptionStatusResponse,
): SubscriptionInconsistency[] => {
  const issues: SubscriptionInconsistency[] = [];

  if (status.is_active && status.is_expired) {
    issues.push("active_and_expired");
  }
  // `CANCELLED` + `is_active` + not expired is a valid grace period: the user
  // turned off auto-renew but is still entitled through the paid period. That is
  // not a contradiction, so don't flag it (it was producing false positives).
  // EXPIRED / NONE / BILLING_ISSUE alongside `is_active` are still real free riders.
  const isCancelledGracePeriod =
    status.status === SubscriptionStatus.CANCELLED && !status.is_expired;
  if (status.is_active && TERMINAL_STATUSES.has(status.status) && !isCancelledGracePeriod) {
    issues.push("entitled_flag_terminal_status");
  }
  // The client's `requiresPaywall` does not treat TRIAL as blocking, so a trial
  // that is no longer active slips through the paywall.
  if (!status.is_active && status.status === SubscriptionStatus.TRIAL) {
    issues.push("trial_not_active");
  }
  if (!status.is_active && status.status === SubscriptionStatus.ACTIVE) {
    issues.push("inactive_flag_active_status");
  }

  return issues;
};

// Non-PII view of the payload. The user id/email are already attached to every
// Sentry event via `setUser`, so we deliberately omit identifiers here.
const nonPiiPayload = (status: SubscriptionStatusResponse) => ({
  status: status.status,
  is_active: status.is_active,
  is_expired: status.is_expired,
  subscription_type: status.subscription_type ?? null,
  expired_at: status.expired_at ?? null,
  product_id: status.product_id ?? null,
  store: status.store ?? null,
  environment: status.environment ?? null,
});

// Dedup so a persistent inconsistency / fail-open doesn't re-fire on every 60s
// refetch or re-render. Keyed by a stable signature for the app session.
const reportedSignatures = new Set<string>();
const reportOnce = (signature: string, emit: () => void) => {
  if (reportedSignatures.has(signature)) return;
  reportedSignatures.add(signature);
  emit();
};

/** Reset dedup state — test-only. */
export const __resetSubscriptionMonitoringDedup = () => {
  reportedSignatures.clear();
};

/**
 * Emit a Sentry warning when a successful status payload is internally
 * contradictory. Returns the detected issues (for testing / call-site use).
 */
export const reportSubscriptionInconsistencies = (
  status: SubscriptionStatusResponse,
): SubscriptionInconsistency[] => {
  const issues = detectSubscriptionInconsistencies(status);
  if (issues.length === 0) return issues;

  const codes = [...issues].sort().join(",");
  const signature = `inconsistent:${codes}:${status.status}:${status.is_active}:${status.is_expired}`;
  reportOnce(signature, () => {
    captureMessage("Inconsistent subscription status", {
      level: "warning",
      tags: {
        feature: "subscription",
        issue: "inconsistent_status",
        inconsistencies: codes,
        subscriptionStatus: status.status,
      },
      extra: { ...nonPiiPayload(status), inconsistencies: issues },
    });
  });

  return issues;
};

/** Structured capture for a failed subscription-status query (network/5xx/etc). */
export const reportSubscriptionStatusError = (error: unknown) => {
  const exception = error instanceof Error ? error : new Error(String(error));
  const httpStatus = error instanceof ApiError ? error.status : undefined;

  captureException(exception, {
    tags: {
      feature: "subscription",
      issue: "status_query_failed",
      errorName: exception.name,
      ...(httpStatus !== undefined ? { httpStatus: String(httpStatus) } : {}),
    },
  });
};

/**
 * Structured capture for a RevenueCat SDK failure (configuration / store /
 * offerings). The SDK often raises these locally on the device, so the raw error
 * carries the real native cause in `underlyingErrorMessage` — surface it instead
 * of the generic "There is an issue with your configuration" string. Deduplicated
 * per session so a persistent store misconfig doesn't re-fire on every refetch.
 *
 * Fields are read defensively (not via the `PurchasesError` type) so this module
 * stays free of a `@/utils/revenue-cat` import and the resulting circular dependency.
 */
export const reportRevenueCatError = (stage: "offerings" | "customer_info", error: unknown) => {
  const exception = error instanceof Error ? error : new Error(String(error));
  const rc = error as Partial<{
    code: string;
    underlyingErrorMessage: string;
    userInfo: { readableErrorCode?: string };
  }> | null;
  const rcCode = typeof rc?.code === "string" ? rc.code : undefined;
  const readableErrorCode = rc?.userInfo?.readableErrorCode;
  const underlyingErrorMessage = rc?.underlyingErrorMessage;

  const signature = `revenuecat:${stage}:${rcCode ?? exception.message}`;
  reportOnce(signature, () => {
    captureException(exception, {
      tags: {
        feature: "subscription",
        issue: "revenuecat_error",
        stage,
        ...(rcCode !== undefined ? { rcCode } : {}),
        ...(readableErrorCode ? { readableErrorCode } : {}),
      },
      ...(underlyingErrorMessage ? { extra: { underlyingErrorMessage } } : {}),
    });
  });
};

/**
 * Emit a Sentry warning when the paywall lets an authenticated, onboarded user
 * through without a positive subscription status — the fail-open the structural
 * fix (#3) will eventually close. `reason` distinguishes a real query error from
 * "we simply had no status".
 */
export const reportPaywallFailOpen = (context: { reason: "query_error" | "no_data" }) => {
  reportOnce(`fail_open:${context.reason}`, () => {
    captureMessage("Paywall failed open (subscription status unknown)", {
      level: "warning",
      tags: {
        feature: "subscription",
        issue: "paywall_fail_open",
        reason: context.reason,
      },
    });
  });
};
